// W2Cook Billing Dashboard — Dashboard tab


import type { Service } from "../data/services";
import type { ConnectorTemplate, LiveResult } from "../connectors/templates";
import type { BudgetLimits } from "../data/environments";
import { tokens as T, getCategoryColor } from "../theme/tokens";
import { Btn, Pill, SectionLabel } from "./ui";

interface Props {
  services:           Service[];
  connectorTemplates: ConnectorTemplate[];
  connectorCfgs:      Record<string, Record<string, string>>;
  liveData:           Record<string, LiveResult>;
  fetching:           Record<string, boolean>;
  limits:             BudgetLimits;
  categoryFilter:     string;
  onCategoryFilter:   (c: string) => void;
  onAddService:       () => void;
  onEditService:      (s: Service) => void;
  onFetchOne:         (id: string) => void;
}

export function DashboardView({
  services, connectorTemplates, connectorCfgs,
  liveData, fetching, limits, categoryFilter,
  onCategoryFilter, onAddService, onEditService, onFetchOne,
}: Props) {

  const allCategories = Array.from(new Set(services.map((s) => s.category))).sort();
  const catColor = (c: string) => getCategoryColor(c, allCategories);

  const active   = services.filter((s) => s.status === "active");
  const paid     = active.filter((s) => s.monthlyCost > 0);
  const free     = active.filter((s) => s.monthlyCost === 0);
  const totalMo  = active.reduce((sum, s) => sum + s.monthlyCost, 0);
  const totalYr  = totalMo * 12;

  const configuredCount = Object.keys(connectorCfgs).length;
  const liveCount       = Object.values(liveData).filter((d) => d.ok).length;

  const categoryTotals: Record<string, number> = {};
  active.forEach((s) => { if (s.monthlyCost > 0) categoryTotals[s.category] = (categoryTotals[s.category] ?? 0) + s.monthlyCost; });

  const categories = ["All", ...allCategories];
  const filtered   = categoryFilter === "All" ? services : services.filter((s) => s.category === categoryFilter);

  const today   = new Date();
  const renewals = services
    .filter((s) => s.renewsOn && s.status === "active")
    .sort((a, b) => new Date(a.renewsOn!).getTime() - new Date(b.renewsOn!).getTime());

  return (
    <>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Monthly Spend",  value: `$${totalMo.toFixed(2)}`,  sub: `${paid.length} paid services`,          accent: T.ember },
          { label: "Annual Run Rate",value: `$${totalYr.toFixed(2)}`,  sub: "projected 12-month",                    accent: T.gold  },
          { label: "Free Tier",      value: free.length,               sub: `of ${active.length} active services`,   accent: T.sage  },
          { label: "Live APIs",      value: `${liveCount}/${configuredCount}`, sub: "connected & responding",         accent: T.blue  },
        ].map((c) => (
          <div key={c.label} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px", borderTop: `3px solid ${c.accent}` }}>
            <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: "0.08em", marginBottom: 8 }}>{c.label.toUpperCase()}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: T.text, lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 6 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Spend bar */}
      {totalMo > 0 && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
          <SectionLabel>SPEND BY CATEGORY</SectionLabel>
          <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 12, gap: 1 }}>
            {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} style={{ flex: amt / totalMo, background: catColor(cat), borderRadius: 2, transition: "flex 0.4s ease" }} />
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
            {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: catColor(cat) }} />
                <span style={{ color: T.textMuted }}>{cat}</span>
                <span style={{ color: T.text, fontWeight: 600 }}>${amt.toFixed(2)}</span>
                <span style={{ color: T.textDim, fontSize: 11 }}>({((amt / totalMo) * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Renewals */}
      {renewals.length > 0 && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
          <SectionLabel>UPCOMING RENEWALS</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {renewals.map((s) => {
              const days = Math.ceil((new Date(s.renewsOn!).getTime() - today.getTime()) / 86_400_000);
              const col  = days <= 7 ? T.ember : days <= 30 ? T.gold : T.textMuted;
              return (
                <div key={s.id} style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", display: "flex", gap: 10, alignItems: "center" }}>
                  <span>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, color: T.text }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: col }}>{days > 0 ? `in ${days} days · ${s.renewsOn}` : "Today"}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.gold, marginLeft: 4 }}>
                    ${s.monthlyCost.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live API status */}
      {Object.keys(liveData).length > 0 && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
          <SectionLabel>LIVE API STATUS</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.entries(liveData).map(([id, d]) => {
              const tpl = connectorTemplates.find((c) => c.id === id);
              return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: T.surfaceAlt, borderRadius: 8, border: `1px solid ${d.ok ? T.border : T.ember + "40"}` }}>
                  <span style={{ fontSize: 16 }}>{tpl?.icon ?? "🔌"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.text }}>{tpl?.name ?? id}</div>
                    <div style={{ fontSize: 11, color: T.textDim }}>{d.note || "—"}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {fetching[id] && <span style={{ fontSize: 11, color: T.textDim }}>polling…</span>}
                    <Pill label={d.ok ? "live" : "error"} color={d.ok ? T.sage : T.ember} />
                    <button onClick={() => onFetchOne(id)} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 14 }}>↻</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter + Add */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {categories.map((cat) => (
            <button key={cat} onClick={() => onCategoryFilter(cat)} style={{
              padding: "6px 13px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              border: categoryFilter === cat ? `1px solid ${T.ember}` : `1px solid ${T.border}`,
              background: categoryFilter === cat ? `${T.ember}20` : T.surface,
              color: categoryFilter === cat ? T.ember : T.textMuted,
            }}>{cat}</button>
          ))}
        </div>
        <Btn variant="primary" onClick={onAddService}>+ Add Service</Btn>
      </div>

      {/* Services table */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.3fr 1fr 1fr 1fr 56px", padding: "10px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.textDim, letterSpacing: "0.07em" }}>
          <div>SERVICE</div><div>CATEGORY</div><div>STATUS</div><div>API</div><div style={{ textAlign: "right" }}>MONTHLY</div><div />
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: T.textDim, fontSize: 14 }}>
            No services in this category.
          </div>
        )}
        {filtered.map((svc, i) => {
          const tpl    = svc.apiConnectorId ? connectorTemplates.find((c) => c.id === svc.apiConnectorId) : null;
          const hasCfg = svc.apiConnectorId ? !!connectorCfgs[svc.apiConnectorId] : false;
          const live   = svc.apiConnectorId ? liveData[svc.apiConnectorId] : null;
          return (
            <div key={svc.id}
              style={{ display: "grid", gridTemplateColumns: "2fr 1.3fr 1fr 1fr 1fr 56px", padding: "13px 20px", borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : "none", alignItems: "center", transition: "background 0.12s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.surfaceAlt)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 18 }}>{svc.icon}</span>
                <div>
                  <div style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{svc.name}</div>
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: 2, maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{svc.note}</div>
                </div>
              </div>
              <div><Pill label={svc.category} color={catColor(svc.category)} /></div>
              <div><Pill label={svc.status} color={svc.status === "active" ? T.sage : svc.status === "paused" ? T.gold : T.ember} /></div>
              <div>
                {tpl ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 14 }}>{tpl.icon}</span>
                    {live
                      ? <Pill label={live.ok ? "live" : "err"} color={live.ok ? T.sage : T.ember} />
                      : hasCfg
                        ? <Pill label="polling" color={T.gold} />
                        : <Pill label="no creds" color={T.textDim} />}
                  </div>
                ) : <span style={{ fontSize: 11, color: T.textDim }}>manual</span>}
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: svc.monthlyCost === 0 ? T.textDim : T.text }}>
                  {svc.monthlyCost === 0 ? "Free" : `$${svc.monthlyCost.toFixed(2)}`}
                </span>
                {svc.monthlyCost > 0 && <div style={{ fontSize: 10, color: T.textDim }}>${(svc.monthlyCost * 12).toFixed(0)}/yr</div>}
                {(() => {
                  const limit = limits.perService[svc.id] ?? svc.budgetLimit;
                  if (!limit || limit === 0) return null;
                  const pct = (svc.monthlyCost / limit) * 100;
                  const col = pct >= 100 ? T.ember : pct >= 80 ? T.gold : T.sage;
                  return (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ height: 3, borderRadius: 2, background: T.border, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: col, transition: "width 0.3s" }} />
                      </div>
                      <div style={{ fontSize: 10, color: col, marginTop: 2 }}>${limit} limit</div>
                    </div>
                  );
                })()}
              </div>
              <div style={{ textAlign: "right" }}>
                <Btn variant="ghost" onClick={() => onEditService(svc)} style={{ padding: "4px 10px", fontSize: 12 }}>Edit</Btn>
              </div>
            </div>
          );
        })}
      </div>

      {/* Planned changes */}
      <div style={{ marginTop: 18, background: `${T.gold}0D`, border: `1px solid ${T.gold}30`, borderRadius: 12, padding: "14px 18px" }}>
        <div style={{ fontSize: 11, color: T.gold, fontWeight: 600, letterSpacing: "0.07em", marginBottom: 8 }}>⚠️ PLANNED COST CHANGES</div>
        {[
          "Kiro Pro Max → Pro downgrade after 6-month period",
          "EAS Build → Starter ($19/mo) before App Store submission",
          "EAS Build → Production ($199/mo) at App Store submission",
          "Firebase, Anthropic API, GCP will scale with usage post-launch",
        ].map((item, i) => (
          <div key={i} style={{ fontSize: 13, color: T.textMuted, display: "flex", gap: 8, marginBottom: 4 }}>
            <span style={{ color: T.gold }}>→</span><span>{item}</span>
          </div>
        ))}
      </div>
    </>
  );
}
